/**
 * Utilitaires plans d'abonnement
 *
 * Rôle : Types PlanName, PlanId, PlanInfo, PlanCard ; constantes PLAN_OPTIONS, PLAN_CARDS ; calculateProPrice, getPlanName.
 * Source unique pour signup, account/informations, tarifs, admin. Technologies : TypeScript
 */
export type PlanName = 'Découverte' | 'Pro' | 'Publicité';

export type PlanId = 'FREE' | 'STANDARD' | 'PREMIUM';

export interface PlanInfo {
  name: PlanName;
}

export const PLAN_OPTIONS: PlanInfo[] = [
  { name: 'Découverte' },
  { name: 'Pro' },
  { name: 'Publicité' },
];

export type PlanCardFeature = { name: string; subtitle?: string };

export type PlanCard = {
  id: PlanId;
  title: string;
  price: string;
  badge?: string;
  badgeClassName?: string;
  features: PlanCardFeature[];
};

/** Cartes de plans affichées sur l'inscription et la page compte (informations). */
export const PLAN_CARDS: PlanCard[] = [
  {
    id: 'FREE',
    title: 'Découverte',
    price: '0 € /mois',
    features: [
      { name: 'Achat de produits B2B sur le marketplace' },
      { name: 'Consultation du listing d\'entreprises' },
      { name: 'Accès aux vidéos produits et aux articles de blog' },
    ],
  },
  {
    id: 'STANDARD',
    title: 'Pro',
    price: '100 € /mois (5 utilisateurs)',
    badge: 'RECOMMANDÉ',
    badgeClassName: 'bg-gradient-to-r from-orange-400 to-red-500 text-white',
    features: [
      { name: 'Logiciel de gestion complet et agréé' },
      { name: 'Site e-commerce professionnel ou vitrine' },
      { name: 'Vente de vos produits sur le marketplace', subtitle: 'Soumis à conditions' },
      { name: 'Répertoire d\'entreprises complet' },
      { name: 'Procédures & vidéos produits exclusives' },
      { name: 'Plateforme de transport intégrée' },
      { name: 'Accès anticipé aux nouvelles fonctionnalités' },
    ],
  },
  {
    id: 'PREMIUM',
    title: 'Offre Publicité',
    price: '200 € /mois',
    badge: 'RÉSERVÉ AUX SPONSORS',
    badgeClassName: 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white',
    features: [
      { name: 'Webinaire hebdomadaire', subtitle: '1 par semaine' },
      { name: 'Article ou vidéo sponsorisé', subtitle: '2 par semaine' },
      { name: 'Accès aux données', subtitle: 'Anonymisées' },
      { name: 'Échange sur le réseau social', subtitle: 'Inclus' },
    ],
  },
];

export function calculateProPrice(numUsers: number): number {
  const basePrice = 100;
  const baseUsers = 5;
  const pricePerExtraUser = 20;
  if (numUsers <= baseUsers) return basePrice;
  return basePrice + (numUsers - baseUsers) * pricePerExtraUser;
}

export function planIdToApiName(id: PlanId): PlanName {
  return id === 'FREE' ? 'Découverte' : id === 'STANDARD' ? 'Pro' : 'Publicité';
}

export function apiNameToPlanId(name: string | null | undefined): PlanId {
  if (!name) return 'FREE';
  const mapping: Record<string, PlanId> = {
    Découverte: 'FREE',
    Pro: 'STANDARD',
    Publicité: 'PREMIUM',
  };
  return mapping[name] || 'FREE';
}

/**
 * Retourne le nom du plan (les vrais noms sont stockés directement en base)
 */
export function getPlanName(plan: string | null | undefined): string {
  if (!plan) return 'Découverte';
  const oldCodes: Record<string, string> = {
    'FREE': 'Découverte',
    'STANDARD': 'Pro',
    'PREMIUM': 'Publicité',
  };
  return oldCodes[plan] || plan;
}

